import { useTranslation } from 'react-i18next';
import type { ReactElement } from 'react';
import { BrandLockup } from './Brand';
import { Avatar } from './Chrome';
import { Icon, type IconName } from './Icons';
import { initialsOf } from './format';

export type LeadHRScreen = 'users' | 'analytics' | 'settings';

const ITEMS: Array<{ key: LeadHRScreen; labelKey: string; icon: IconName }> = [
  { key: 'analytics', labelKey: 'com_ui_leadhr_nav_analytics', icon: 'analytics' },
  { key: 'users', labelKey: 'com_ui_leadhr_nav_users', icon: 'licences' },
  { key: 'settings', labelKey: 'com_ui_leadhr_nav_settings', icon: 'company' },
];

export default function Sidebar({
  active,
  onNavigate,
  onSignOut,
  companyName,
  userName,
}: {
  active: LeadHRScreen;
  onNavigate: (screen: LeadHRScreen) => void;
  onSignOut: () => void;
  companyName?: string;
  userName?: string;
}): ReactElement {
  const { t } = useTranslation();

  const navItem = (key: LeadHRScreen, label: string, icon: IconName) => (
    <button
      key={key}
      type="button"
      className="lh-navitem"
      onClick={() => onNavigate(key)}
      aria-current={key === active ? 'page' : undefined}
    >
      <Icon name={icon} />
      <span>{label}</span>
    </button>
  );

  return (
    <aside
      className="w-full flex-none p-2 md:sticky md:top-0 md:h-screen md:w-[280px] md:overflow-y-auto md:pr-0"
      aria-label={t('com_ui_leadhr_title')}
    >
      <nav
        className="flex h-full flex-col justify-between rounded-2xl"
        style={{ background: 'var(--lh-lgreen)' }}
        aria-label={t('com_ui_leadhr_title')}
      >
        <div>
          <div className="p-6">
            <BrandLockup />
          </div>
          {userName ? (
            <div className="hidden flex-col gap-2 p-6 md:flex">
              <Avatar tone="dark" size={64} initials={initialsOf(userName, userName)} />
              <div className="min-w-0">
                <p
                  className="m-0 break-words text-xl"
                  style={{ font: '400 20px/1.4 var(--lh-font-book)', letterSpacing: '-0.4px' }}
                >
                  {userName}
                </p>
                <p className="m-0 leading-[1.6]">{t('com_ui_leadhr_role')}</p>
                {companyName ? (
                  <p className="lh-sub m-0" style={{ color: 'var(--lh-ink-soft)' }}>
                    {companyName}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}
          <div className="flex flex-col gap-1 p-2">
            {ITEMS.map((item) => navItem(item.key, t(item.labelKey), item.icon))}
          </div>
        </div>
        <div className="flex flex-col gap-1 p-2">
          <button type="button" className="lh-navitem" onClick={onSignOut}>
            <Icon name="logout" />
            <span>{t('com_ui_leadhr_sign_out')}</span>
          </button>
        </div>
      </nav>
    </aside>
  );
}
